import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  CheckCircle2, 
  Star, 
  Flame, 
  Heart,
  Trophy,
  Sparkles,
  BookOpen,
  ArrowRight,
  Zap,
  Crown,
  Lock
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

/**
 * Animated Demo Component - Updated with new features
 * Shows: Dashboard → Transversal Course → Daily Question → Quiz → Badges → Premium
 */

const AnimatedDemo = () => {
  const { t, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Translations for the demo
  const demoT = {
    fr: {
      step1: "Étape 1", step2: "Étape 2", step3: "Étape 3", step4: "Étape 4", step5: "Étape 5", step6: "Étape 6",
      play: "Lancer", pause: "Pause", live: "DÉMO",
      // Screen 1 - Dashboard
      welcome: "Bienvenue sur Spirify",
      chooseYourPath: "Choisissez votre parcours",
      allReligions: "8 cours disponibles",
      courses: ["Islam", "Christianisme", "Judaïsme", "Spiritualité", "Bouddhisme", "Hindouisme", "Taoïsme", "Transversal"],
      lessons: "leçons",
      // Screen 2 - Transversal
      transversalTitle: "Cours Transversal",
      transversalSubtitle: "6 parcours thématiques cross-traditions",
      parcours: [
        { icon: "🔥", name: "Souffrance & libération", lessons: 5 },
        { icon: "⚖️", name: "Action juste", lessons: 5 },
        { icon: "🧠", name: "Non-dualité & illusion", lessons: 5 },
        { icon: "👁️", name: "Ego & illusion spirituelle", lessons: 5 },
        { icon: "🤍", name: "Compassion & éthique", lessons: 5 },
        { icon: "🌅", name: "Éveil authentique", lessons: 5 }
      ],
      // Screen 3 - Daily Question
      dailyQuestionTitle: "Question du jour",
      dailyQuestionSubtitle: "Une minute pour élargir ta compréhension",
      dailyQuestion: "Dans le bouddhisme, l'ego est surtout :",
      dailyOptions: ["Une entité réelle", "Une construction conditionnée", "Une âme", "Une énergie divine"],
      correctXP: "+15 XP",
      streakDays: "jours de suite",
      // Screen 4 - Quiz
      quizTitle: "Quiz interactif",
      quizSubtitle: "Testez vos connaissances",
      quizQuestion: "Que signifie 'Shahada' ?",
      quizOptions: ["Le jeûne du Ramadan", "La profession de foi", "La prière quotidienne", "Le pèlerinage à La Mecque"],
      // Screen 5 - Badges
      badgesTitle: "Badges & Récompenses",
      badgesSubtitle: "La constance transforme la compréhension",
      clarityBadge: "Clarté",
      clarityDesc: "7 jours consécutifs",
      wisdomBadge: "Sagesse",
      wisdomDesc: "30 jours consécutifs",
      totalXP: "XP total",
      streak: "Série",
      // Screen 6 - Premium
      premiumTitle: "Contenu Premium",
      premiumSubtitle: "Débloquez l'accès complet",
      premiumFeatures: ["Accès illimité à tous les cours", "Cours Transversal complet", "Progression sans limite"],
      premiumPrice: "4,99€/mois",
      premiumCTA: "Passer à Premium"
    },
    en: {
      step1: "Step 1", step2: "Step 2", step3: "Step 3", step4: "Step 4", step5: "Step 5", step6: "Step 6",
      play: "Play", pause: "Pause", live: "DEMO",
      welcome: "Welcome to Spirify",
      chooseYourPath: "Choose your path",
      allReligions: "8 courses available",
      courses: ["Islam", "Christianity", "Judaism", "Spirituality", "Buddhism", "Hinduism", "Taoism", "Transversal"],
      lessons: "lessons",
      transversalTitle: "Transversal Course",
      transversalSubtitle: "6 thematic cross-tradition paths",
      parcours: [
        { icon: "🔥", name: "Suffering & Liberation", lessons: 5 },
        { icon: "⚖️", name: "Right Action", lessons: 5 },
        { icon: "🧠", name: "Non-duality & Illusion", lessons: 5 },
        { icon: "👁️", name: "Ego & Spiritual Illusion", lessons: 5 },
        { icon: "🤍", name: "Compassion & Ethics", lessons: 5 },
        { icon: "🌅", name: "Authentic Awakening", lessons: 5 }
      ],
      dailyQuestionTitle: "Question of the Day",
      dailyQuestionSubtitle: "One minute to expand your understanding",
      dailyQuestion: "In Buddhism, the ego is mainly:",
      dailyOptions: ["A real entity", "A conditioned construct", "A soul", "A divine energy"],
      correctXP: "+15 XP",
      streakDays: "days streak",
      quizTitle: "Interactive Quiz",
      quizSubtitle: "Test your knowledge",
      quizQuestion: "What does 'Shahada' mean?",
      quizOptions: ["Ramadan fasting", "Profession of faith", "Daily prayer", "Pilgrimage to Mecca"],
      badgesTitle: "Badges & Rewards",
      badgesSubtitle: "Consistency transforms understanding",
      clarityBadge: "Clarity",
      clarityDesc: "7 consecutive days",
      wisdomBadge: "Wisdom",
      wisdomDesc: "30 consecutive days",
      totalXP: "Total XP",
      streak: "Streak",
      premiumTitle: "Premium Content",
      premiumSubtitle: "Unlock full access",
      premiumFeatures: ["Unlimited access to all courses", "Complete Transversal course", "Unlimited progression"],
      premiumPrice: "€4.99/month",
      premiumCTA: "Go Premium"
    },
    es: {
      step1: "Paso 1", step2: "Paso 2", step3: "Paso 3", step4: "Paso 4", step5: "Paso 5", step6: "Paso 6",
      play: "Iniciar", pause: "Pausa", live: "DEMO",
      welcome: "Bienvenido a Spirify",
      chooseYourPath: "Elige tu camino",
      allReligions: "8 cursos disponibles",
      courses: ["Islam", "Cristianismo", "Judaísmo", "Espiritualidad", "Budismo", "Hinduismo", "Taoísmo", "Transversal"],
      lessons: "lecciones",
      transversalTitle: "Curso Transversal",
      transversalSubtitle: "6 recorridos temáticos cross-tradiciones",
      parcours: [
        { icon: "🔥", name: "Sufrimiento y liberación", lessons: 5 },
        { icon: "⚖️", name: "Acción justa", lessons: 5 },
        { icon: "🧠", name: "No-dualidad e ilusión", lessons: 5 },
        { icon: "👁️", name: "Ego e ilusión espiritual", lessons: 5 },
        { icon: "🤍", name: "Compasión y ética", lessons: 5 },
        { icon: "🌅", name: "Despertar auténtico", lessons: 5 }
      ],
      dailyQuestionTitle: "Pregunta del día",
      dailyQuestionSubtitle: "Un minuto para ampliar tu comprensión",
      dailyQuestion: "En el budismo, el ego es principalmente:",
      dailyOptions: ["Una entidad real", "Una construcción condicionada", "Un alma", "Una energía divina"],
      correctXP: "+15 XP",
      streakDays: "días seguidos",
      quizTitle: "Quiz interactivo",
      quizSubtitle: "Pon a prueba tus conocimientos",
      quizQuestion: "¿Qué significa 'Shahada'?",
      quizOptions: ["El ayuno del Ramadán", "La profesión de fe", "La oración diaria", "La peregrinación a La Meca"],
      badgesTitle: "Badges y Recompensas",
      badgesSubtitle: "La constancia transforma la comprensión",
      clarityBadge: "Claridad",
      clarityDesc: "7 días consecutivos",
      wisdomBadge: "Sabiduría",
      wisdomDesc: "30 días consecutivos",
      totalXP: "XP total",
      streak: "Racha",
      premiumTitle: "Contenido Premium",
      premiumSubtitle: "Desbloquea el acceso completo",
      premiumFeatures: ["Acceso ilimitado a todos los cursos", "Curso Transversal completo", "Progresión sin límites"],
      premiumPrice: "4,99€/mes",
      premiumCTA: "Pasar a Premium"
    },
    it: {
      step1: "Passo 1", step2: "Passo 2", step3: "Passo 3", step4: "Passo 4", step5: "Passo 5", step6: "Passo 6",
      play: "Avvia", pause: "Pausa", live: "DEMO",
      welcome: "Benvenuto su Spirify",
      chooseYourPath: "Scegli il tuo percorso",
      allReligions: "8 corsi disponibili",
      courses: ["Islam", "Cristianesimo", "Ebraismo", "Spiritualità", "Buddismo", "Induismo", "Taoismo", "Transversal"],
      lessons: "lezioni",
      transversalTitle: "Corso Transversal",
      transversalSubtitle: "6 percorsi tematici cross-tradizioni",
      parcours: [
        { icon: "🔥", name: "Sofferenza e liberazione", lessons: 5 },
        { icon: "⚖️", name: "Azione giusta", lessons: 5 },
        { icon: "🧠", name: "Non-dualità e illusione", lessons: 5 },
        { icon: "👁️", name: "Ego e illusione spirituale", lessons: 5 },
        { icon: "🤍", name: "Compassione ed etica", lessons: 5 },
        { icon: "🌅", name: "Risveglio autentico", lessons: 5 }
      ],
      dailyQuestionTitle: "Domanda del giorno",
      dailyQuestionSubtitle: "Un minuto per ampliare la tua comprensione",
      dailyQuestion: "Nel buddismo, l'ego è principalmente:",
      dailyOptions: ["Un'entità reale", "Una costruzione condizionata", "Un'anima", "Un'energia divina"],
      correctXP: "+15 XP",
      streakDays: "giorni di fila",
      quizTitle: "Quiz interattivo",
      quizSubtitle: "Metti alla prova le tue conoscenze",
      quizQuestion: "Cosa significa 'Shahada'?",
      quizOptions: ["Il digiuno del Ramadan", "La professione di fede", "La preghiera quotidiana", "Il pellegrinaggio alla Mecca"],
      badgesTitle: "Badge e Ricompense",
      badgesSubtitle: "La costanza trasforma la comprensione",
      clarityBadge: "Chiarezza",
      clarityDesc: "7 giorni consecutivi",
      wisdomBadge: "Saggezza",
      wisdomDesc: "30 giorni consecutivi",
      totalXP: "XP totale",
      streak: "Serie",
      premiumTitle: "Contenuto Premium",
      premiumSubtitle: "Sblocca l'accesso completo",
      premiumFeatures: ["Accesso illimitato a tutti i corsi", "Corso Transversal completo", "Progressione senza limiti"],
      premiumPrice: "4,99€/mese",
      premiumCTA: "Passa a Premium"
    },
    pt: {
      step1: "Passo 1", step2: "Passo 2", step3: "Passo 3", step4: "Passo 4", step5: "Passo 5", step6: "Passo 6",
      play: "Iniciar", pause: "Pausar", live: "DEMO",
      welcome: "Bem-vindo ao Spirify",
      chooseYourPath: "Escolha seu caminho",
      allReligions: "8 cursos disponíveis",
      courses: ["Islã", "Cristianismo", "Judaísmo", "Espiritualidade", "Budismo", "Hinduísmo", "Taoísmo", "Transversal"],
      lessons: "lições",
      transversalTitle: "Curso Transversal",
      transversalSubtitle: "6 percursos temáticos cross-tradições",
      parcours: [
        { icon: "🔥", name: "Sofrimento e libertação", lessons: 5 },
        { icon: "⚖️", name: "Ação justa", lessons: 5 },
        { icon: "🧠", name: "Não-dualidade e ilusão", lessons: 5 },
        { icon: "👁️", name: "Ego e ilusão espiritual", lessons: 5 },
        { icon: "🤍", name: "Compaixão e ética", lessons: 5 },
        { icon: "🌅", name: "Despertar autêntico", lessons: 5 }
      ],
      dailyQuestionTitle: "Pergunta do dia",
      dailyQuestionSubtitle: "Um minuto para expandir sua compreensão",
      dailyQuestion: "No budismo, o ego é principalmente:",
      dailyOptions: ["Uma entidade real", "Uma construção condicionada", "Uma alma", "Uma energia divina"],
      correctXP: "+15 XP",
      streakDays: "dias seguidos",
      quizTitle: "Quiz interativo",
      quizSubtitle: "Teste seus conhecimentos",
      quizQuestion: "O que significa 'Shahada'?",
      quizOptions: ["O jejum do Ramadã", "A profissão de fé", "A oração diária", "A peregrinação a Meca"],
      badgesTitle: "Badges e Recompensas",
      badgesSubtitle: "A constância transforma a compreensão",
      clarityBadge: "Clareza",
      clarityDesc: "7 dias consecutivos",
      wisdomBadge: "Sabedoria",
      wisdomDesc: "30 dias consecutivos",
      totalXP: "XP total",
      streak: "Sequência",
      premiumTitle: "Conteúdo Premium",
      premiumSubtitle: "Desbloqueie o acesso completo",
      premiumFeatures: ["Acesso ilimitado a todos os cursos", "Curso Transversal completo", "Progressão sem limites"],
      premiumPrice: "4,99€/mês",
      premiumCTA: "Passar para Premium"
    },
    ar: {
      step1: "الخطوة 1", step2: "الخطوة 2", step3: "الخطوة 3", step4: "الخطوة 4", step5: "الخطوة 5", step6: "الخطوة 6",
      play: "تشغيل", pause: "إيقاف", live: "عرض",
      welcome: "مرحباً بك في Spirify",
      chooseYourPath: "اختر مسارك",
      allReligions: "8 دورات متاحة",
      courses: ["الإسلام", "المسيحية", "اليهودية", "الروحانية", "البوذية", "الهندوسية", "الطاوية", "Transversal"],
      lessons: "دروس",
      transversalTitle: "الدورة العابرة",
      transversalSubtitle: "6 مسارات موضوعية عبر التقاليد",
      parcours: [
        { icon: "🔥", name: "المعاناة والتحرر", lessons: 5 },
        { icon: "⚖️", name: "الفعل الصحيح", lessons: 5 },
        { icon: "🧠", name: "اللاثنائية والوهم", lessons: 5 },
        { icon: "👁️", name: "الأنا والوهم الروحي", lessons: 5 },
        { icon: "🤍", name: "الرحمة والأخلاق", lessons: 5 },
        { icon: "🌅", name: "الصحوة الحقيقية", lessons: 5 }
      ],
      dailyQuestionTitle: "سؤال اليوم",
      dailyQuestionSubtitle: "دقيقة واحدة لتوسيع فهمك",
      dailyQuestion: "في البوذية، الأنا هي بالأساس:",
      dailyOptions: ["كيان حقيقي", "بناء مشروط", "روح", "طاقة إلهية"],
      correctXP: "+15 XP",
      streakDays: "أيام متتالية",
      quizTitle: "اختبار تفاعلي",
      quizSubtitle: "اختبر معرفتك",
      quizQuestion: "ماذا تعني 'الشهادة'؟",
      quizOptions: ["صيام رمضان", "شهادة الإيمان", "الصلاة اليومية", "الحج إلى مكة"],
      badgesTitle: "الشارات والمكافآت",
      badgesSubtitle: "الثبات يحول الفهم",
      clarityBadge: "الوضوح",
      clarityDesc: "7 أيام متتالية",
      wisdomBadge: "الحكمة",
      wisdomDesc: "30 يومًا متتاليًا",
      totalXP: "إجمالي XP",
      streak: "السلسلة",
      premiumTitle: "محتوى مميز",
      premiumSubtitle: "افتح الوصول الكامل",
      premiumFeatures: ["وصول غير محدود لجميع الدورات", "دورة Transversal كاملة", "تقدم بلا حدود"],
      premiumPrice: "4.99€/شهر",
      premiumCTA: "الترقية للمميز"
    }
  };

  const dt = demoT[language] || demoT.fr;

  const steps = [
    { id: 0, title: dt.welcome, subtitle: dt.allReligions, component: <DashboardScreen dt={dt} /> },
    { id: 1, title: dt.transversalTitle, subtitle: dt.transversalSubtitle, component: <TransversalScreen dt={dt} /> },
    { id: 2, title: dt.dailyQuestionTitle, subtitle: dt.dailyQuestionSubtitle, component: <DailyQuestionScreen dt={dt} /> },
    { id: 3, title: dt.quizTitle, subtitle: dt.quizSubtitle, component: <QuizScreen dt={dt} /> },
    { id: 4, title: dt.badgesTitle, subtitle: dt.badgesSubtitle, component: <BadgesScreen dt={dt} /> },
    { id: 5, title: dt.premiumTitle, subtitle: dt.premiumSubtitle, component: <PremiumScreen dt={dt} /> }
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => (prev < steps.length - 1 ? prev + 1 : 0));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isPlaying, steps.length]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying && currentStep === steps.length - 1) setCurrentStep(0);
  };

  return (
    <div className="relative">
      <div className="relative aspect-video bg-gradient-to-br from-gray-50 to-white rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-200">
        <div className="absolute inset-0 transition-opacity duration-700">
          {steps[currentStep].component}
        </div>

        {!isPlaying && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-sm">
            <button onClick={handlePlayPause} className="w-20 h-20 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
              <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </button>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
          <div className="flex items-center justify-between text-white">
            <div className="flex-1">
              <h4 className="font-bold text-lg mb-1">{steps[currentStep].title}</h4>
              <p className="text-sm text-gray-300">{steps[currentStep].subtitle}</p>
            </div>
            <button onClick={handlePlayPause} className="ml-4 px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg backdrop-blur-sm transition-colors">
              {isPlaying ? dt.pause : dt.play}
            </button>
          </div>
          <div className="flex items-center space-x-2 mt-4">
            {steps.map((step, index) => (
              <button key={step.id} onClick={() => setCurrentStep(index)} className={`h-2 rounded-full transition-all ${index === currentStep ? 'w-8 bg-purple-500' : 'w-2 bg-white/40 hover:bg-white/60'}`} />
            ))}
          </div>
        </div>
      </div>

      {isPlaying && (
        <div className="absolute top-4 right-4 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full flex items-center space-x-1 animate-pulse">
          <div className="w-2 h-2 bg-white rounded-full"></div>
          <span>{dt.live}</span>
        </div>
      )}
    </div>
  );
};

// Screen 1 - Dashboard with 8 courses
const DashboardScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-gray-50 to-white p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
      <Sparkles className="w-4 h-4 mr-2" />{dt.step1}
    </Badge>
    <h2 className="text-2xl font-bold mb-2">{dt.chooseYourPath}</h2>
    <p className="text-gray-600 mb-6">{dt.allReligions}</p>
    <div className="grid grid-cols-4 gap-3 w-full max-w-4xl">
      {['☪️', '✝️', '✡️', '🕉️', '☸️', '🕉️', '☯️', '🌐'].map((emoji, i) => (
        <Card key={i} className={`p-3 text-center hover:scale-105 transition-all cursor-pointer ${i === 7 ? 'border-purple-300 bg-purple-50' : ''}`}>
          <div className="text-3xl mb-1">{emoji}</div>
          <div className="text-xs font-medium truncate">{dt.courses[i]}</div>
          {i === 7 && <Badge className="mt-1 text-[10px] bg-purple-600">NEW</Badge>}
        </Card>
      ))}
    </div>
  </div>
);

// Screen 2 - Transversal Course with 6 parcours
const TransversalScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-purple-50 to-white p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
      <BookOpen className="w-4 h-4 mr-2" />{dt.step2}
    </Badge>
    <h2 className="text-2xl font-bold mb-2">🌐 {dt.transversalTitle}</h2>
    <p className="text-gray-600 mb-4">{dt.transversalSubtitle}</p>
    <div className="grid grid-cols-3 gap-3 w-full max-w-4xl">
      {dt.parcours.map((p, i) => (
        <Card key={i} className="p-3 hover:shadow-lg transition-all animate-slide-up" style={{ animationDelay: `${i * 100}ms` }}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{p.icon}</span>
            <div>
              <div className="text-sm font-semibold">{p.name}</div>
              <div className="text-xs text-gray-500">{p.lessons} {dt.lessons}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

// Screen 3 - Daily Question
const DailyQuestionScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-cyan-50 to-white p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">
      <Sparkles className="w-4 h-4 mr-2" />{dt.step3}
    </Badge>
    <div className="flex items-center gap-2 mb-4">
      <h2 className="text-2xl font-bold">✨ {dt.dailyQuestionTitle}</h2>
      <Badge variant="outline">🔥 7 {dt.streakDays}</Badge>
    </div>
    <Card className="w-full max-w-2xl p-6 shadow-xl">
      <h3 className="text-lg font-bold mb-4">{dt.dailyQuestion}</h3>
      <div className="space-y-2">
        {dt.dailyOptions.map((opt, i) => (
          <button key={i} className={`w-full p-3 rounded-lg border-2 text-left text-sm transition-all ${i === 1 ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            {opt}
            {i === 1 && <CheckCircle2 className="w-4 h-4 text-green-600 inline ml-2" />}
          </button>
        ))}
      </div>
      <div className="mt-4 text-center text-green-600 font-bold text-xl animate-pulse">{dt.correctXP}</div>
    </Card>
  </div>
);

// Screen 4 - Quiz
const QuizScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-blue-50 to-white p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-blue-50 text-blue-700 border-blue-200">
      <Zap className="w-4 h-4 mr-2" />{dt.step4}
    </Badge>
    <div className="flex items-center gap-4 mb-4">
      <div className="flex">{[...Array(5)].map((_, i) => <Heart key={i} className="w-5 h-5 text-red-500 fill-red-500" />)}</div>
      <div className="text-sm"><Star className="w-4 h-4 inline text-amber-500" /> 150 XP</div>
    </div>
    <Card className="w-full max-w-2xl p-6 shadow-xl">
      <h3 className="text-lg font-bold mb-4">{dt.quizQuestion}</h3>
      <div className="space-y-2">
        {dt.quizOptions.map((opt, i) => (
          <button key={i} className={`w-full p-3 rounded-lg border-2 text-left text-sm transition-all ${i === 1 ? 'border-green-500 bg-green-50 animate-pulse' : 'border-gray-200'}`}>
            {opt}
            {i === 1 && <CheckCircle2 className="w-4 h-4 text-green-600 inline ml-2" />}
          </button>
        ))}
      </div>
    </Card>
  </div>
);

// Screen 5 - Badges
const BadgesScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-amber-50 to-white p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-amber-50 text-amber-700 border-amber-200">
      <Trophy className="w-4 h-4 mr-2" />{dt.step5}
    </Badge>
    <h2 className="text-2xl font-bold mb-6">{dt.badgesTitle}</h2>
    <div className="grid grid-cols-2 gap-6 w-full max-w-2xl mb-6">
      <Card className="p-6 text-center bg-gradient-to-br from-cyan-50 to-blue-50 animate-slide-up">
        <div className="text-5xl mb-2">✨</div>
        <div className="text-xl font-bold text-cyan-700">{dt.clarityBadge}</div>
        <div className="text-sm text-gray-600">{dt.clarityDesc}</div>
        <Badge className="mt-2 bg-cyan-600">Unlocked!</Badge>
      </Card>
      <Card className="p-6 text-center bg-gradient-to-br from-green-50 to-emerald-50 animate-slide-up" style={{ animationDelay: '150ms' }}>
        <div className="text-5xl mb-2">🌿</div>
        <div className="text-xl font-bold text-green-700">{dt.wisdomBadge}</div>
        <div className="text-sm text-gray-600">{dt.wisdomDesc}</div>
        <Badge variant="outline" className="mt-2">23/30</Badge>
      </Card>
    </div>
    <div className="flex gap-8">
      <div className="text-center"><Flame className="w-8 h-8 text-orange-500 mx-auto" /><div className="font-bold">23</div><div className="text-xs text-gray-500">{dt.streak}</div></div>
      <div className="text-center"><Star className="w-8 h-8 text-amber-500 mx-auto" /><div className="font-bold">2,450</div><div className="text-xs text-gray-500">{dt.totalXP}</div></div>
    </div>
  </div>
);

// Screen 6 - Premium
const PremiumScreen = ({ dt }) => (
  <div className="w-full h-full bg-gradient-to-br from-purple-50 to-indigo-50 p-6 flex flex-col items-center justify-center animate-fade-in">
    <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
      <Crown className="w-4 h-4 mr-2" />{dt.step6}
    </Badge>
    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mb-4">
      <Crown className="w-8 h-8 text-white" />
    </div>
    <h2 className="text-2xl font-bold mb-2">{dt.premiumTitle}</h2>
    <p className="text-gray-600 mb-6">{dt.premiumSubtitle}</p>
    <div className="space-y-2 mb-6">
      {dt.premiumFeatures.map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          <span>{f}</span>
        </div>
      ))}
    </div>
    <div className="bg-purple-100 px-4 py-2 rounded-full mb-4">
      <span className="font-bold text-purple-700">{dt.premiumPrice}</span>
    </div>
    <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8">
      <Crown className="w-4 h-4 mr-2" />{dt.premiumCTA}
    </Button>
  </div>
);

export default AnimatedDemo;
